/**
 * Утилита для динамического обновления иконок PWA
 * Позволяет изменять иконки приложения в зависимости от выбранной команды
 */

import { applyManifestLinkHref } from './earlyPwaManifest';

class DynamicIconsManager {
  constructor() {
    this.defaultIcons = {
      '192x192': '/img/icons/logo192.png',
      '512x512': '/img/icons/logo512.png',
      'favicon': '/favicon.ico'
    };
    this.defaultManifestHref = `${this.baseUrl()}manifest.json`;
  }

  baseUrl() {
    const b = (typeof process !== 'undefined' && process.env && process.env.BASE_URL) || '/';
    return b.endsWith('/') ? b : `${b}/`;
  }

  /**
   * Тот же origin, что у страницы — в dev прокси отдаёт манифест с backend.
   * Chrome для «Установить приложение» не использует manifest с blob:-URL.
   */
  applyWebManifest(teamId) {
    applyManifestLinkHref(teamId);
  }

  resetWebManifest() {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) {
      link.href = this.defaultManifestHref;
    }
  }

  updateAppleWebAppTitle(title) {
    let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'apple-mobile-web-app-title');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', title);
  }

  /**
   * Обновляет иконки PWA на основе логотипа команды
   * @param {string} teamLogoUrl - URL логотипа команды
   * @param {string} teamName - Название команды
   */
  async updateAppIcons(teamLogoUrl, teamName, teamId) {
    if (!teamLogoUrl) {
      this.resetToDefaultIcons();
      return;
    }

    try {
      // Создаем временные ссылки на иконки
      const iconUrls = await this.generateTeamIcons(teamLogoUrl, teamName);
      
      // Обновляем favicon
      this.updateFavicon(iconUrls.favicon);
      
      // Обновляем PWA иконки
      this.updatePWAIcons(iconUrls);
      
      // Обновляем Apple touch icon
      this.updateAppleTouchIcon(iconUrls.appleTouch);

      if (teamId != null && String(teamId) !== '') {
        this.applyWebManifest(teamId);
      } else {
        this.resetWebManifest();
      }
      this.updateAppleWebAppTitle(teamName.length > 20 ? `${teamName.slice(0, 19)}…` : teamName);
      
      console.log(`Иконки обновлены для команды: ${teamName}`);
    } catch (error) {
      console.error('Ошибка при обновлении иконок:', error);
      this.resetToDefaultIcons();
    }
  }

  /**
   * Обновляет иконки PWA на основе кастомного бота команды
   * @param {string} botIconUrl - URL иконки кастомного бота
   * @param {string} teamName - Название команды
   */
  async updateAppIconsFromBot(botIconUrl, teamName, teamId) {
    if (!botIconUrl) {
      this.resetToDefaultIcons();
      return;
    }

    try {
      // Создаем временные ссылки на иконки из иконки бота
      const iconUrls = await this.generateTeamIcons(botIconUrl, teamName);
      
      // Обновляем favicon
      this.updateFavicon(iconUrls.favicon);
      
      // Обновляем PWA иконки
      this.updatePWAIcons(iconUrls);
      
      // Обновляем Apple touch icon
      this.updateAppleTouchIcon(iconUrls.appleTouch);

      if (teamId != null && String(teamId) !== '') {
        this.applyWebManifest(teamId);
      } else {
        this.resetWebManifest();
      }
      this.updateAppleWebAppTitle(teamName.length > 20 ? `${teamName.slice(0, 19)}…` : teamName);
      
      console.log(`Иконки обновлены на основе бота команды: ${teamName}`);
    } catch (error) {
      console.error('Ошибка при обновлении иконок из бота:', error);
      this.resetToDefaultIcons();
    }
  }

  /**
   * Генерирует иконки разных размеров из логотипа команды
   */
  async generateTeamIcons(logoUrl, teamName) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Загружаем изображение логотипа
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          const icons = {};
          
          // Генерируем иконки разных размеров
          const sizes = [
            { name: 'favicon', size: 32 },
            { name: '192x192', size: 192 },
            { name: '512x512', size: 512 },
            { name: 'appleTouch', size: 180 }
          ];
          
          sizes.forEach(({ name, size }) => {
            canvas.width = size;
            canvas.height = size;
            
            // Очищаем canvas
            ctx.clearRect(0, 0, size, size);
            
            // Рисуем фон (круглый для favicon, квадратный для остальных)
            if (name === 'favicon') {
              ctx.beginPath();
              ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
              ctx.fillStyle = '#ffffff';
              ctx.fill();
            } else {
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, size, size);
            }
            
            // Вычисляем размеры для вставки изображения
            const scale = Math.min(size / img.width, size / img.height) * 0.8;
            const scaledWidth = img.width * scale;
            const scaledHeight = img.height * scale;
            const x = (size - scaledWidth) / 2;
            const y = (size - scaledHeight) / 2;
            
            // Вставляем изображение
            ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
            
            // Конвертируем в data URL
            icons[name] = canvas.toDataURL('image/png');
          });
          
          resolve(icons);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Не удалось загрузить изображение логотипа'));
      };
      
      img.src = logoUrl;
    });
  }

  /**
   * Обновляет favicon
   */
  updateFavicon(iconUrl) {
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.href = iconUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = iconUrl;
      document.head.appendChild(link);
    }
  }

  /**
   * Обновляет PWA иконки в manifest
   */
  updatePWAIcons(iconUrls) {
    // Обновляем существующие ссылки на иконки
    const iconLinks = document.querySelectorAll('link[rel="icon"][sizes]');
    iconLinks.forEach(link => {
      const sizes = link.getAttribute('sizes');
      if (sizes === '192x192' && iconUrls['192x192']) {
        link.href = iconUrls['192x192'];
      } else if (sizes === '512x512' && iconUrls['512x512']) {
        link.href = iconUrls['512x512'];
      }
    });
  }

  /**
   * Обновляет Apple touch icon
   */
  updateAppleTouchIcon(iconUrl) {
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleTouchIcon) {
      appleTouchIcon.href = iconUrl;
      appleTouchIcon.setAttribute('sizes', '180x180');
    } else {
      const link = document.createElement('link');
      link.rel = 'apple-touch-icon';
      link.setAttribute('sizes', '180x180');
      link.href = iconUrl;
      document.head.appendChild(link);
    }
  }

  /**
   * Возвращает иконки к значениям по умолчанию
   */
  resetToDefaultIcons() {
    this.resetWebManifest();
    this.updateAppleWebAppTitle('Hockey App');

    this.updateFavicon(this.defaultIcons.favicon);
    
    // Обновляем PWA иконки
    const iconLinks = document.querySelectorAll('link[rel="icon"][sizes]');
    iconLinks.forEach(link => {
      const sizes = link.getAttribute('sizes');
      if (sizes === '192x192') {
        link.href = this.defaultIcons['192x192'];
      } else if (sizes === '512x512') {
        link.href = this.defaultIcons['512x512'];
      }
    });
    
    // Обновляем Apple touch icon
    const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleTouchIcon) {
      appleTouchIcon.href = this.defaultIcons['192x192'];
    }
  }

  /**
   * Сохраняет настройки иконок в localStorage
   */
  saveIconSettings(teamId, teamLogoUrl, teamName) {
    const settings = {
      teamId,
      teamLogoUrl,
      teamName,
      timestamp: Date.now()
    };
    localStorage.setItem('teamIconSettings', JSON.stringify(settings));
  }

  /**
   * Загружает настройки иконок из localStorage
   */
  loadIconSettings() {
    const settings = localStorage.getItem('teamIconSettings');
    if (settings) {
      try {
        return JSON.parse(settings);
      } catch (error) {
        console.error('Ошибка при загрузке настроек иконок:', error);
        localStorage.removeItem('teamIconSettings');
      }
    }
    return null;
  }

  /**
   * Восстанавливает иконки команды при загрузке приложения
   */
  async restoreTeamIcons() {
    const settings = this.loadIconSettings();
    if (settings && settings.teamLogoUrl) {
      // Дольше живут настройки — иначе после перезахода на сайт ярлык снова с дефолтной иконкой
      const isExpired = Date.now() - settings.timestamp > 30 * 24 * 60 * 60 * 1000;
      if (!isExpired) {
        // Проверяем, является ли это иконкой бота (по URL)
        if (settings.teamLogoUrl.includes('t.me/i/userpic/')) {
          await this.updateAppIconsFromBot(settings.teamLogoUrl, settings.teamName, settings.teamId);
        } else {
          await this.updateAppIcons(settings.teamLogoUrl, settings.teamName, settings.teamId);
        }
        return settings.teamId;
      } else {
        localStorage.removeItem('teamIconSettings');
      }
    }
    return null;
  }
}

// Экспортируем экземпляр класса
export const dynamicIconsManager = new DynamicIconsManager(); 